import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import {
  brand,
  breakpoints,
  ink,
  surface,
  tones,
  touchFeedback,
  touchTarget,
  textSize,
  displayType,
  titleType,
} from '../../../styles/brand';

export const Container = styled.div`
  ${touchFeedback}

  padding: 2.38rem;

  > a:first-child {
    background: none;
    display: flex;
    align-items: center;
    color: ${ink};
    text-align: center;
    margin-bottom: 1rem;
    font-family: 'RedhatDisplay-Bold';
    font-size: 1.1em;
    width: fit-content;
    transition: opacity 0.3s;
    opacity: 0.8;

    svg {
      margin-right: 0.4rem;
    }

    &:hover {
      opacity: 1;

      > svg {
        opacity: 1;
      }
    }
  }

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
  }
`;

/**
 * Barra compacta do celular: entra deslizando quando o nome do torneio sai da
 * tela (como o título grande do iOS ao recolher). Fixa, porque o layout
 * principal usa overflow-x: hidden e isso desliga o position: sticky.
 */
export const CompactNavBar = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: ${breakpoints.md}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: calc(2.75rem + env(safe-area-inset-top, 0px));
    padding: env(safe-area-inset-top, 0px)
      max(0.75rem, env(safe-area-inset-right, 0px)) 0
      max(0.75rem, env(safe-area-inset-left, 0px));
    background: ${transparentize(0.22, brand.white)};
    backdrop-filter: blur(18px) saturate(180%);
    -webkit-backdrop-filter: blur(18px) saturate(180%);
    box-shadow: 0 1px 0 ${surface.line};
    transform: translateY(${({ $visible }) => ($visible ? '0' : '-110%')});
    transition: transform 280ms cubic-bezier(0.32, 0.72, 0, 1);

    a,
    button {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      min-height: ${touchTarget};
      padding: 0 0.25rem;
      border: 0;
      background: none;
      font-family: 'RedHatDisplay-Bold';
      font-size: ${textSize(0.9)};
      color: ${brand.blue};
      cursor: pointer;
    }

    strong {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: 'RedHatDisplay-Bold';
      font-size: ${textSize(0.95)};
      color: ${ink};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (prefers-reduced-transparency: reduce) {
    background: ${brand.white};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

export const MainCard = styled.div`
  background: #fff;
  border: 1px solid ${surface.line};
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 0.75rem;
  }
`;

/**
 * Arte, informações e telão. A arte mantém os 16.25rem atuais; o telão fica
 * ao lado (≥ 80rem), embaixo dentro do card (48–80rem) ou compacto no celular.
 */
export const MainCardContent = styled.div`
  display: grid;
  grid-template-columns: 16.25rem minmax(0, 1fr) 17rem;
  grid-template-areas: 'art info board';
  gap: 2.5rem;
  align-items: start;

  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: 16.25rem minmax(0, 1fr);
    grid-template-areas:
      'art info'
      'board board';
    row-gap: 1.5rem;
  }

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'art'
      'info'
      'board';
    gap: 0.9rem;
  }
`;

export const TrophyImageWrapper = styled.div`
  grid-area: art;
  flex: 0 0 16.25rem;
  width: 16.25rem;
  height: 16.25rem;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 2;
  }

  svg {
    z-index: 1;
  }

  @media (max-width: ${breakpoints.md}) {
    flex: unset;
    align-self: center;
    justify-self: center;
    width: 6rem;
    height: 6rem;
  }
`;

export const TournamentInfo = styled.div`
  grid-area: info;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/** Sentinela logo abaixo do nome: quando sai da tela, a barra compacta entra. */
export const TitleSentinel = styled.span`
  display: block;
  height: 1px;
  margin-top: -0.75rem;
`;

export const TournamentTitle = styled.h1`
  font-family: 'RedHatDisplay-ExtraBold';
  font-size: ${textSize(1.8)};
  ${displayType}
  color: ${ink};
  margin: 0;

  @media (max-width: ${breakpoints.md}) {
    font-size: ${textSize(1.35)};
    text-align: center;
  }
`;

export const TournamentMeta = styled.p`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.9)};
  color: ${tones.gray.text};
  margin: 0;

  svg {
    width: 1.1rem;
    height: 1.1rem;
    flex: none;
    color: ${ink};
  }

  @media (max-width: ${breakpoints.md}) {
    font-size: ${textSize(0.85)};
    justify-content: center;
  }
`;

export const MetaGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.25rem;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: center;
  }
`;

export const ModalitiesLabel = styled.p`
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.9)};
  color: ${ink};
  margin: 0;
`;

export const ModalitiesRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: ${breakpoints.md}) {
    gap: 0.6rem;
  }
`;

export const ModalityCard = styled.div<{ $active?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  border: 2px solid ${brand.orange};
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  min-width: 11.25rem;

  @media (max-width: ${breakpoints.md}) {
    min-width: unset;
    width: 100%;
    padding: 0.6rem 0.75rem;
    gap: 0.6rem;
  }
`;

export const ModalityImage = styled.img`
  width: 3.2rem;
  height: 3.2rem;
  object-fit: contain;

  @media (max-width: ${breakpoints.md}) {
    width: 2.5rem;
    height: 2.5rem;
  }
`;

export const ModalityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ModalityName = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.95)};
  color: ${ink};
`;

export const ModalityMeta = styled.span`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.78)};
  color: ${tones.gray.text};
`;

export const CategoryBadgeRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

export const CategoryBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: ${tones.blue.soft};
  color: ${brand.blue};
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: ${textSize(0.72)};
  font-family: 'RedHatDisplay-Medium';
`;

export const ActionsArea = styled.div`
  margin-top: 0.5rem;
`;

export const ScheduleSection = styled.div`
  background: #fff;
  border: 1px solid ${surface.line};
  border-radius: 1rem;
  padding: 1.5rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

export const ScheduleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;

  h2 {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1.4)};
    ${titleType}
    color: ${ink};
  }

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    /* Celular: título centralizado, como as abas logo abaixo (FE-71). */
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    h2 {
      font-size: ${textSize(1.2)};
    }
  }
`;

export const ScheduleHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .secondary-button {
    height: 2.8rem;
    font-size: ${textSize(0.9)};
    padding: 0 1.2rem;
    border-radius: 0.55rem;
    border: 1.5px solid ${brand.blue};
    color: ${brand.blue};
    display: flex;
    align-items: center;
    gap: 0.4rem;

    &:hover {
      color: ${brand.green};
      border: 1px solid ${brand.green};
      svg {
        transition: transform 0.3s;
        stroke: ${brand.green};
      }
    }
  }

  .primary-button {
    height: 2.8rem;
    font-size: ${textSize(0.9)};
    padding: 0 1.2rem;
    border-radius: 0.55rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    box-shadow: 0px 3px 8px rgba(255, 152, 0, 0.3);
  }

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
    flex-direction: column;
    gap: 0.6rem;

    .secondary-button,
    .primary-button {
      width: 100%;
      justify-content: center;
      height: 3rem;
      font-size: ${textSize(0.95)};
    }
  }
`;

export const ScheduleTableWrapper = styled.div`
  border: 1px solid ${surface.line};
  border-radius: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 0.5rem;
  -webkit-overflow-scrolling: touch;
`;

export const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 43.75rem;

  @media (max-width: ${breakpoints.md}) {
    thead th:first-child {
      position: sticky;
      left: 0;
      z-index: 3;
      background: ${surface.page};
      box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
    }

    tbody td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background: #fff;
      box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
    }
  }
`;

export const ScheduleTableHead = styled.thead`
  background: ${surface.page};
  border-bottom: 1px solid ${surface.line};
`;

export const ScheduleTh = styled.th`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.85)};
  color: ${tones.gray.text};
  padding: 0.9rem 1rem;
  text-align: center;
  white-space: nowrap;
  font-weight: normal;
`;

/**
 * Linha neutra por padrão: o status já aparece no pill. Só a equipe
 * desclassificada ganha fundo, porque é a exceção que precisa saltar aos
 * olhos numa tabela de dezenas de linhas.
 */
export const ScheduleTableRow = styled.tr<{ $disqualified?: boolean }>`
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.15s;
  background: ${({ $disqualified }) =>
    $disqualified ? tones.red.soft : 'transparent'};

  &:last-child {
    border-bottom: none;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $disqualified }) =>
        $disqualified ? tones.red.soft : surface.page};
    }
  }
`;

export const TeamCell = styled.span<{ $struck: boolean }>`
  color: ${({ $struck }) => ($struck ? tones.red.text : 'inherit')};
  text-decoration: ${({ $struck }) => ($struck ? 'line-through' : 'none')};
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const ScheduleTd = styled.td`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.9)};
  color: ${ink};
  padding: 0.75rem 1rem;
  text-align: center;
  white-space: nowrap;
`;

/**
 * Ações primárias da linha como link de texto: a tabela tem uma por linha,
 * e trinta pílulas coloridas viram ruído. A cor fica para o status.
 */
const rowLink = css`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  min-height: 2rem;
  padding: 0 0.5rem;
  border: 0;
  border-radius: 0.5rem;
  background: none;
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.85)};
  color: ${tones.blue.text};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s;

  @media (hover: hover) {
    &:hover {
      background: ${tones.blue.soft};
    }
  }
`;

export const ViewRoundButton = styled.button`
  ${rowLink}
`;

export const ViewResultButton = styled.button`
  ${rowLink}
`;

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
`;

export const Spinner = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border: 3px solid ${surface.line};
  border-top-color: ${brand.orange};
  border-radius: 50%;
  animation: spin 0.75s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ── Próxima Rodada ──────────────────────────────────────────────────────────

export const NextSlotCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: #fff8ef;
  border: 1.5px solid ${brand.orange};
  border-radius: 0.75rem;
  padding: 1rem 1.5rem;
  margin-bottom: 1.25rem;

  @media (max-width: ${breakpoints.md}) {
    padding: 0.75rem 1rem;
  }
`;

export const NextSlotLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.72)};
  color: ${tones.orange.text};
  text-transform: uppercase;
  letter-spacing: 0.06em;

  svg {
    width: 1rem;
    height: 1rem;
    flex: none;
  }
`;

export const NextSlotTeam = styled.p`
  font-family: 'RedHatDisplay-ExtraBold';
  font-size: ${textSize(1.25)};
  ${titleType}
  color: ${ink};
  margin: 0;

  @media (max-width: ${breakpoints.md}) {
    font-size: ${textSize(1.05)};
  }
`;

export const NextSlotMeta = styled.p`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.85)};
  color: ${tones.gray.text};
  margin: 0;
`;

// ── Abas ─────────────────────────────────────────────────────────────────────

export const TabsArea = styled.div`
  margin-bottom: 1.25rem;

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    justify-content: center;
  }
`;

// ── Cronograma em cards (celular) ─────────────────────────────────────────────

export const SlotCardList = styled.div`
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const SlotGroupLabel = styled.h3`
  margin: 0.75rem 0 0.1rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.75)};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${tones.gray.text};

  &:first-child {
    margin-top: 0;
  }
`;

export const BreakCard = styled.p`
  margin: 0;
  padding: 0.6rem 0.9rem;
  border-radius: 0.9rem;
  border: 1px dashed ${surface.line};
  text-align: center;
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.8)};
  color: ${tones.gray.text};
`;

export const SlotCardAction = styled.button<{
  $tone?: 'primary' | 'ghost';
}>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-height: 2.5rem;
  min-width: ${touchTarget};
  padding: 0 0.85rem;
  border-radius: 0.65rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.8)};
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 100ms ease-out;
  border: 1px solid transparent;
  ${({ $tone = 'primary' }) =>
    $tone === 'primary'
      ? `background: ${brand.orange}; color: ${brand.white};`
      : `background: ${tones.blue.soft}; color: ${tones.blue.text};`}

  &:active {
    transform: scale(0.97);
  }
`;

export const SlotCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex: none;
`;

// ── Filtros em chips (celular) ────────────────────────────────────────────────

export const FilterChips = styled.div`
  display: flex;
  gap: 0.4rem;
  overflow-x: auto;
  scrollbar-width: none;
  margin: 0 -1rem 0.9rem;
  padding: 0 1rem 0.1rem;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FilterChip = styled.button<{ $active: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0 0.85rem;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? ink : surface.line)};
  background: ${({ $active }) => ($active ? ink : brand.white)};
  color: ${({ $active }) => ($active ? brand.white : ink)};
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.82)};
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
`;

export const MobileSearch = styled.div`
  margin-bottom: 0.6rem;

  > div {
    width: 100%;
  }
`;

/** "X de Y rodadas" do celular, logo abaixo dos chips de filtro. */
export const MobileFilterSummary = styled.p`
  margin: -0.4rem 0 0.75rem;
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.78)};
  color: ${tones.gray.text};
`;

/** Filtros ativos sem nenhuma rodada: diz por quê em vez de uma lista vazia. */
export const FilteredEmptyNote = styled.p`
  margin: 0.5rem 0 1rem;
  padding: 1.25rem 1rem;
  border: 1px dashed ${surface.line};
  border-radius: 0.75rem;
  text-align: center;
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.88)};
  color: ${tones.gray.text};
`;

// ── Agrupamento por rodada ───────────────────────────────────────────────────

export const RoundHeaderRow = styled.tr`
  background: ${tones.blue.soft};
  border-top: 1.5px solid ${tones.blue.border};
  border-bottom: 1px solid ${tones.blue.border};
`;

export const RoundHeaderCell = styled.td`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.78)};
  color: ${brand.blue};
  padding: 0.45rem 1rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.65rem;
  flex-wrap: wrap;
  background: ${surface.page};
  border: 1px solid ${surface.line};
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
`;

export const FilterRowTitle = styled.span`
  flex-basis: 100%;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.78)};
  color: ${tones.gray.text};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.1rem;
`;

export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

export const FilterFieldLabel = styled.span`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.8)};
  color: ${tones.gray.text};
  padding-left: 0.1rem;
`;

interface IFilterInputProps {
  $isFocused: boolean;
}

export const FilterInput = styled.div<IFilterInputProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1.5px solid
    ${({ $isFocused }) => ($isFocused ? brand.blue : surface.line)};
  border-radius: 0.55rem;
  padding: 0 0.85rem;
  height: 2.75rem;
  background: #fff;
  width: 200px;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  box-shadow: ${({ $isFocused }) =>
    $isFocused ? '0 0 0 3px rgba(30, 136, 229, 0.1)' : 'none'};

  svg {
    flex-shrink: 0;
  }

  input {
    border: none;
    background: transparent;
    font-family: 'RedHatDisplay-Regular';
    font-size: ${textSize(0.88)};
    color: ${ink};
    width: 100%;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
    }
  }
`;

export const ClearFiltersLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: ${tones.red.soft};
  border: 1px solid ${tones.red.border};
  border-radius: 20px;
  cursor: pointer;
  color: ${tones.red.text};
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.78)};
  padding: 0.3rem 0.75rem;
  margin-left: auto;
  white-space: nowrap;
  transition: background 0.2s;

  &:hover {
    background: #fecaca;
  }
`;

export const FilterResultCount = styled.span`
  font-family: 'RedHatDisplay-Medium';
  font-size: ${textSize(0.78)};
  color: ${tones.gray.text};
  white-space: nowrap;
  background: ${surface.page};
  border: 1px solid ${surface.line};
  border-radius: 20px;
  padding: 0.25rem 0.65rem;
`;

// ── Status + dicas de pré-requisitos ────────────────────────────────────────

export const StatusAndHintsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;

  @media (max-width: ${breakpoints.md}) {
    justify-content: center;
  }
`;

export const PrerequisitePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 1.9rem;
  padding: 0 0.7rem;
  border-radius: 20px;
  font-size: ${textSize(0.75)};
  font-family: 'RedHatDisplay-Medium';

  svg {
    width: 0.95rem;
    height: 0.95rem;
    flex: none;
  }

  white-space: nowrap;
  cursor: pointer;
  border: 1px solid #fbbf24;
  background: #fffbeb;
  color: #92400e;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: #fef3c7;
    border-color: #f59e0b;
  }
`;

// ── Empty state do cronograma ────────────────────────────────────────────────

export const ScheduleEmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 3rem 2rem;

  .primary-button {
    height: 2.8rem;
    font-size: ${textSize(0.9)};
    padding: 0 1.2rem;
    border-radius: 0.55rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    box-shadow: 0px 3px 8px rgba(255, 152, 0, 0.3);
  }
`;

export const ScheduleEmptyTitle = styled.p`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(1.05)};
  color: ${ink};
  margin: 0;
`;

export const ScheduleEmptyDesc = styled.p`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.88)};
  color: ${tones.gray.text};
  margin: 0;
  max-width: 440px;
`;

export const PrerequisiteChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
`;

export const PrerequisiteChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 1rem;
  border-radius: 0.55rem;
  font-size: ${textSize(0.85)};
  font-family: 'RedHatDisplay-Medium';
  white-space: nowrap;
  cursor: pointer;
  border: 1.5px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
  transition: background 0.15s;

  &:hover {
    background: #fef3c7;
  }
`;

// ── Avaliação só para juízes ─────────────────────────────────────────────────

export const JudgeOnlyNote = styled.span`
  display: inline-block;
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.78)};
  color: ${tones.gray.text};
  white-space: nowrap;
  cursor: default;
`;
